/* Custom roll label configurator with custom dropdowns (listbox pattern).
 * Variant data comes from the ink-roll-configurator-state section, rendered by Shopify for the
 * selected option values, so prices always match the real variants (also above 250 variants).
 * Material and finish share one product option: "Gloss"/"Matte" (coated paper) or
 * "<Material> · <Finish>", optionally "Coated paper · <Finish>".
 */
(() => {
  const SEP = ' · ';
  const MATERIALS = {
    paper: {
      label: 'Coated paper',
      note: 'Permanent adhesive. Not waterproof, so use it on dry packaging such as boxes, bags and mailers.'
    },
    pp: {
      label: 'PP synthetic',
      note: 'Resists moisture and tearing. The removable adhesive peels from suitable smooth surfaces.'
    },
    bopp: {
      label: 'Clear BOPP',
      note: 'Clear film with a removable adhesive. Unprinted areas show the glass, plastic or metal underneath.'
    }
  };
  const idForLabel = (label) => Object.keys(MATERIALS).find((id) => MATERIALS[id].label === label);
  const escapeHtml = (text) => String(text).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /* Accessible single-select dropdown: button + listbox. */
  class Dropdown {
    constructor(root, onSelect) {
      this.root = root;
      this.name = root.dataset.ircDd;
      this.button = root.querySelector('.irc__dd-btn');
      this.list = root.querySelector('[role="listbox"]');
      this.valueEl = root.querySelector('[data-irc-dd-value]');
      this.onSelect = onSelect;
      this.button.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
      this.button.addEventListener('keydown', (event) => {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.open();
        }
      });
      this.list.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option && option.getAttribute('aria-disabled') !== 'true') this.pick(option);
      });
      this.list.addEventListener('keydown', (event) => this.onKey(event));
      this.list.addEventListener('mousemove', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.setActive(option, false);
      });
    }

    options() { return [...this.list.querySelectorAll('[role="option"]')]; }
    enabled() { return this.options().filter((o) => o.getAttribute('aria-disabled') !== 'true'); }
    isOpen() { return !this.list.hidden; }
    selected() { return this.list.querySelector('[aria-selected="true"]'); }

    open() {
      Dropdown.closeAll(this);
      this.list.hidden = false;
      this.root.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      this.setActive(this.selected() || this.enabled()[0], true);
      this.list.focus({ preventScroll: true });
    }

    close(focusButton = false) {
      if (!this.isOpen()) return;
      this.list.hidden = true;
      this.root.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
      if (focusButton) this.button.focus({ preventScroll: true });
    }

    setActive(option, scroll) {
      if (!option) return;
      this.options().forEach((o) => o.classList.toggle('is-active', o === option));
      this.list.setAttribute('aria-activedescendant', option.id);
      if (scroll) option.scrollIntoView({ block: 'nearest' });
    }

    onKey(event) {
      const items = this.enabled();
      const current = this.list.querySelector('.is-active');
      let index = items.indexOf(current);
      if (event.key === 'ArrowDown') index = Math.min(items.length - 1, index + 1);
      else if (event.key === 'ArrowUp') index = Math.max(0, index - 1);
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = items.length - 1;
      else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (current) this.pick(current);
        return;
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      } else if (event.key === 'Tab') {
        this.close();
        return;
      } else return;
      event.preventDefault();
      this.setActive(items[index], true);
    }

    pick(option) {
      this.select(option.dataset.value);
      this.close(true);
      this.onSelect(this.name, option.dataset.value);
    }

    select(value) {
      this.options().forEach((o) => o.setAttribute('aria-selected', String(o.dataset.value === value)));
    }

    static closeAll(except) {
      Dropdown.all.forEach((dd) => { if (dd !== except) dd.close(); });
    }
  }
  Dropdown.all = [];
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-irc-dd]')) Dropdown.closeAll();
  });

  class InkRollConfigurator {
    constructor(root) {
      this.root = root;
      this.mode = root.dataset.mode || 'roll';
      this.unit = root.dataset.unit || 'labels';
      this.form = document.getElementById(root.dataset.formId) || root.closest('form');
      this.notice = root.querySelector('[data-irc-notice]');
      try {
        const combos = JSON.parse(root.querySelector('[data-irc-combos]')?.textContent || 'null');
        this.combos = Array.isArray(combos) ? new Set(combos) : null;
      } catch (error) { this.combos = null; }
      this.money = new Intl.NumberFormat(root.dataset.locale || undefined, { style: 'currency', currency: root.dataset.currency || 'USD' });
      this.number = new Intl.NumberFormat(root.dataset.locale || undefined);
      this.cache = new Map();
      this.requestId = 0;
      this.addButton = root.querySelector('[data-irc-add]');
      this.addLabel = root.querySelector('[data-irc-add-label]');
      this.status = root.querySelector('[data-irc-status]');

      this.state = this.parseState(root.querySelector('[data-irc-state]')?.textContent);
      if (!this.state) return;
      this.names = this.state.options.map((option) => option.name);
      this.idx = {
        size: this.names.indexOf('Size'),
        material: this.names.indexOf(this.names.includes('Material') ? 'Material' : 'Finish'),
        qty: this.names.indexOf('Quantity')
      };
      const options = this.state.variant.options;
      this.selection = {
        size: options[this.idx.size],
        qty: options[this.idx.qty],
        ...this.splitMaterial(options[this.idx.material])
      };
      this.cache.set(this.key(this.selection), this.state);

      this.dropdowns = {};
      root.querySelectorAll('[data-irc-dd]').forEach((el) => {
        const dd = new Dropdown(el, (name, value) => this.onPick(name, value));
        this.dropdowns[dd.name] = dd;
        Dropdown.all.push(dd);
      });
      root.querySelectorAll('[data-irc-qty-label]').forEach((el) => {
        const n = Number(el.closest('[data-value]')?.dataset.value);
        if (Number.isFinite(n)) el.textContent = `${this.number.format(n)} ${this.unit}`;
      });
      ['unwind', 'core'].forEach((name) => this.renderButton(name));

      this.addButton?.addEventListener('click', () => this.addToCart());
      document.addEventListener('ink:roll-material', (event) => {
        if (!this.materialAvailable(event.detail)) return;
        this.onPick('material', event.detail);
        this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      this.render();
      this.applyIncoming();
    }

    // Size/finish/quantity carried over from another material page (?ink=size|finish|qty).
    applyIncoming() {
      let incoming = null;
      try { incoming = new URL(window.location.href).searchParams.get('ink'); } catch (error) { incoming = null; }
      if (!incoming) return;
      const [size, finish, qty] = incoming.split('|');
      if (!this.value(this.idx.size, size) || !this.value(this.idx.material, finish) || !this.value(this.idx.qty, qty)) return;
      const next = { ...this.selection, size, finish, qty };
      if (!this.comboAvailable(next)) next.qty = this.nearestQty(next);
      if (!next.qty) return;
      this.selection = next;
      this.render();
      this.load(next);
    }

    comboAvailable(sel) {
      if (!this.combos) return true;
      return this.combos.has([sel.size, this.materialValue(sel.material, sel.finish), sel.qty].join('|'));
    }

    nearestQty(sel) {
      const wanted = Number(sel.qty);
      const options = this.dropdowns.qty ? this.dropdowns.qty.options().map((o) => o.dataset.value) : [];
      const open = options.filter((q) => this.comboAvailable({ ...sel, qty: q }));
      open.sort((a, b) => Math.abs(Number(a) - wanted) - Math.abs(Number(b) - wanted) || Number(a) - Number(b));
      return open[0] || null;
    }

    setNotice(message) {
      if (!this.notice) return;
      this.notice.textContent = message;
      this.notice.hidden = !message;
    }

    parseState(text) {
      try { return JSON.parse(text || ''); } catch (error) { return null; }
    }

    splitMaterial(value) {
      const text = String(value || '');
      if (!text.includes(SEP)) return { material: 'paper', finish: text };
      const [label, finish] = text.split(SEP);
      return { material: idForLabel(label) || 'paper', finish };
    }

    value(optionIndex, name) {
      return this.state.options[optionIndex]?.values.find((value) => value.name === name) || null;
    }

    materialValue(material, finish) {
      if (material === 'paper') {
        return this.value(this.idx.material, finish) ? finish : `${MATERIALS.paper.label}${SEP}${finish}`;
      }
      return `${MATERIALS[material].label}${SEP}${finish}`;
    }

    materialAvailable(material) {
      return this.state.options[this.idx.material].values.some((value) => this.splitMaterial(value.name).material === material);
    }

    key(sel) {
      return [sel.size, sel.material, sel.finish, sel.qty].join('|');
    }

    onPick(name, value) {
      const dd = this.dropdowns[name];
      if (dd?.root.hasAttribute('data-irc-nav')) {
        const option = dd.options().find((o) => o.dataset.value === value);
        if (!option?.dataset.url || option.dataset.url === this.root.dataset.productUrl) return;
        const url = new URL(option.dataset.url, window.location.origin);
        const sel = this.selection;
        url.searchParams.set('ink', [sel.size, sel.finish, sel.qty].join('|'));
        const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        window.location.assign(url.toString());
        return;
      }
      if (dd?.root.hasAttribute('data-irc-property')) {
        dd.select(value);
        const input = dd.root.querySelector('input[type="hidden"]');
        if (input) input.value = value;
        this.renderButton(name);
        return;
      }
      this.choose(name, value);
    }

    async choose(field, value) {
      if (this.selection[field] === value) return;
      const next = { ...this.selection, [field]: value };
      this.setNotice('');
      if (field !== 'qty' && !this.comboAvailable(next)) {
        const qty = this.nearestQty(next);
        if (!qty) return;
        const sizeLabel = (this.dropdowns.size?.options().find((o) => o.dataset.value === next.size)?.dataset.label || next.size).trim();
        this.setNotice(`${this.number.format(Number(next.qty))} ${this.unit} is not offered in ${sizeLabel}, so we switched to ${this.number.format(Number(qty))}.`);
        next.qty = qty;
      }
      this.selection = next;
      this.render();
      await this.load(this.selection);
    }

    async load(sel) {
      const cached = this.cache.get(this.key(sel));
      if (cached) {
        this.state = cached;
        this.render();
        return;
      }
      const ids = [];
      ids[this.idx.size] = this.value(this.idx.size, sel.size)?.id;
      ids[this.idx.material] = this.value(this.idx.material, this.materialValue(sel.material, sel.finish))?.id;
      ids[this.idx.qty] = this.value(this.idx.qty, sel.qty)?.id;
      if (ids.some((id) => !id)) return;
      const request = ++this.requestId;
      this.root.classList.add('is-loading');
      try {
        const url = `${this.root.dataset.productUrl}?option_values=${ids.join(',')}&section_id=ink-roll-configurator-state`;
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const state = this.parseState(doc.querySelector('[data-irc-state]')?.textContent);
        if (!state) throw new Error('No state');
        this.cache.set(this.key(sel), state);
        if (request !== this.requestId) return;
        this.state = state;
        this.setStatus('');
      } catch (error) {
        if (request === this.requestId) this.setStatus('Prices could not be updated. Please check your connection and try again.', 'error');
      } finally {
        if (request === this.requestId) {
          this.root.classList.remove('is-loading');
          this.render();
        }
      }
    }

    format(cents) {
      return this.money.format(Number(cents) / 100);
    }

    /* Closed-state content of a dropdown button, built from its selected option. */
    renderButton(name) {
      const dd = this.dropdowns[name];
      if (!dd) return;
      const option = dd.selected();
      if (!option) return;
      const label = option.dataset.label || option.querySelector('strong')?.textContent || option.dataset.value;
      const price = option.querySelector('[data-irc-opt-price]')?.textContent || '';
      let html = '';
      if (option.dataset.thumb) html += `<img src="${escapeHtml(option.dataset.thumb)}" alt="" width="28" height="28">`;
      html += `<span class="irc__dd-text">${escapeHtml(label)}</span>`;
      if (option.hasAttribute('data-popular')) html += '<span class="irc__badge">Most popular</span>';
      if (price) html += `<span class="irc__dd-price">${escapeHtml(price)}</span>`;
      dd.valueEl.innerHTML = html;
    }

    render() {
      const sel = this.selection;
      const variant = this.state.variant;
      const current = this.splitMaterial(variant.options[this.idx.material]);
      const synced = variant.options[this.idx.size] === sel.size && variant.options[this.idx.qty] === sel.qty
        && current.material === sel.material && current.finish === sel.finish;

      const material = this.mode === 'roll' ? this.dropdowns.material : null;
      material?.options().forEach((option) => {
        const id = option.dataset.value;
        const available = this.materialAvailable(id);
        option.setAttribute('aria-disabled', String(!available));
        const price = option.querySelector('[data-irc-opt-price]');
        if (!available) price.textContent = 'Unavailable';
        else if (synced) {
          const cents = this.value(this.idx.material, this.materialValue(id, sel.finish))?.variant?.price;
          if (cents != null) price.textContent = this.format(cents);
        }
      });
      this.dropdowns.qty?.options().forEach((option) => {
        const open = this.comboAvailable({ ...sel, qty: option.dataset.value });
        option.hidden = !open;
        option.setAttribute('aria-disabled', String(!open));
        if (!synced || !open) return;
        const cents = this.value(this.idx.qty, option.dataset.value)?.variant?.price;
        if (cents == null) return;
        option.querySelector('[data-irc-opt-price]').textContent = this.format(cents);
        const each = option.querySelector('[data-irc-opt-each]');
        if (each) each.textContent = `${this.format(cents / Number(option.dataset.value))} each`;
      });

      material?.select(sel.material);
      this.dropdowns.finish?.select(sel.finish);
      this.dropdowns.size?.select(sel.size);
      this.dropdowns.qty?.select(sel.qty);
      ['material', 'finish', 'size', 'qty'].forEach((name) => this.renderButton(name));

      const note = this.root.querySelector('[data-irc-material-note]');
      if (note && this.mode === 'roll') note.textContent = MATERIALS[sel.material].note;

      if (synced) {
        this.root.querySelector('[data-irc-total]').textContent = this.format(variant.price);
        this.root.querySelector('[data-irc-count]').textContent = `${this.number.format(Number(sel.qty))} ${this.unit}`;
        this.root.querySelector('[data-irc-each]').textContent = `${this.format(variant.price / Number(sel.qty))} each`;
        const idInput = this.form?.querySelector('input[name="id"]');
        if (idInput) idInput.value = variant.id;
        this.updateUrl(variant.id);
      }
      this.addButton.disabled = !synced || variant.available === false;
    }

    updateUrl(id) {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('variant') === String(id)) return;
        url.searchParams.set('variant', id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) { /* keep the current URL */ }
    }

    setStatus(message, state = '') {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.state = state;
      this.status.hidden = !message;
    }

    artworkReady() {
      const upload = this.root.querySelector('[data-ink-artwork-upload]');
      if (!upload || !window.validateInkArtworkUpload?.(this.form)) return false;
      const value = (selector) => upload.querySelector(selector)?.value?.trim() || '';
      const state = upload.querySelector('[data-cloud-upload-status]')?.dataset.state;
      return state === 'ready' && value('[data-cloud-upload-id]') && value('[data-cloud-version-id]') && value('[data-cloud-artwork-key]');
    }

    properties() {
      const properties = {};
      if (!this.form) return properties;
      for (const [name, entry] of new FormData(this.form)) {
        if (entry instanceof File) continue;
        const match = name.match(/^properties\[(.+)]$/);
        const value = String(entry).trim();
        if (match && value) properties[match[1]] = value;
      }
      return properties;
    }

    async addToCart() {
      if (this.busy) return;
      if (!this.artworkReady()) {
        const upload = this.root.querySelector('[data-ink-artwork-upload]');
        upload?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        upload?.querySelector('[data-upload-error]')?.removeAttribute('hidden');
        this.setStatus('Please upload your artwork and wait for the upload to finish.', 'error');
        return;
      }
      const variant = this.state.variant;
      this.busy = true;
      this.addButton.disabled = true;
      this.addLabel.textContent = 'Adding to cart…';
      this.setStatus('');
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variant.id), quantity: 1, properties: this.properties() }] })
        });
        const text = await response.text();
        let result = {};
        try { result = text ? JSON.parse(text) : {}; } catch (error) { result = {}; }
        if (!response.ok) {
          throw new Error(response.status === 429
            ? 'Too many requests. Please wait a few seconds and try again.'
            : (result.description || result.message || 'This item could not be added to your cart.'));
        }
        this.setStatus('Added. Opening your cart…', 'success');
        window.location.assign(this.root.dataset.cartUrl || '/cart');
      } catch (error) {
        this.busy = false;
        this.addButton.disabled = false;
        this.addLabel.textContent = 'Add to cart';
        this.setStatus(error.message, 'error');
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-roll-configurator]').forEach((root) => {
      if (root._inkRollConfigurator) return;
      root._inkRollConfigurator = new InkRollConfigurator(root);
    });
  };
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-irc-choose]');
    if (!trigger) return;
    event.preventDefault();
    document.dispatchEvent(new CustomEvent('ink:roll-material', { detail: trigger.dataset.ircChoose }));
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
